import { handleFetchError } from "./errorHandler";
import Config from "../Config";
import AuthService from "../services/AuthService";

/**
 * Validate URL to prevent undefined calls
 * @param {string} url - The URL to validate
 * @returns {boolean} - True if valid, throws error if invalid
 */
function validateUrl(url) {
  // Make sure url is a string before using includes
  if (typeof url !== 'string') {
    console.error('validateUrl received non-string URL:', url);
    throw new Error(`Invalid API URL type: ${typeof url}`);
  }
  
  // STRICT VALIDATION: Check for undefined or null anywhere in URL
  if (url.includes('undefined') || url.includes('null')) {
    console.error(`Prevented API call with invalid URL: ${url}`);
    throw new Error(`Invalid API URL contains undefined/null: ${url}`);
  }
  
  // STRICT USER ID VALIDATION: Detect any URLs with a userId pattern
  
  // First check simple /{userId} pattern
  const directUserIdMatch = url.match(/^\/([^\/]+)$/);
  if (directUserIdMatch) {
    const userId = directUserIdMatch[1];
    if (userId === '' || !AuthService.isValidUserId(userId)) {
      console.error(`Prevented API call with invalid direct userId in URL: ${url}`);
      throw new Error(`Invalid direct userId in API URL: ${url}`);
    }
  }
  
  // Then check /{userId}/something pattern
  const nestedUserIdMatch = url.match(/^\/([^\/]+)\//);
  if (nestedUserIdMatch) {
    const userId = nestedUserIdMatch[1];
    if (userId === '' || !AuthService.isValidUserId(userId)) {
      console.error(`Prevented API call with invalid nested userId in URL: ${url}`);
      throw new Error(`Invalid nested userId in API URL: ${url}`);
    }
  }
  
  // Special handling for certain URLs that don't need userId validation
  const whitelistedPaths = [
    '/login', 
    '/signup', 
    '/reset-password', 
    '/regenerateMultipass',
    '/',
    ''
  ];
  
  if (!whitelistedPaths.includes(url)) {
    // For all other URLs, double-check they don't have empty segments
    if (url.includes('//')) {
      console.error(`Prevented API call with empty URL segment: ${url}`);
      throw new Error(`Invalid URL with empty segment: ${url}`);
    }
  }
  
  return true;
}

/**
 * Enhanced fetch utility that prevents race conditions
 * Waits for authentication to be ready before making API calls
 */
async function fetchUtil(url, options = {}) {
  // Wait for authentication to be ready if this is an authenticated request
  // This prevents race conditions where components make API calls before auth is ready
  if (options.token && !options.skipAuthCheck) {
    try {
      await AuthService.waitForAuth();
      console.log('fetchUtil: Auth ready, proceeding with request');
    } catch (error) {
      console.error('fetchUtil: Error waiting for auth:', error);
      throw new Error('Authentication not ready');
    }
  }
  
  // Validate URL to prevent undefined calls
  validateUrl(url);
  
  const {
    method = "GET",
    body = null,
    token = null,
    image = false,
    abortSignal = null,
    credentials = "same-origin",
    externalApi = false,
    skipAuthCheck = false,
  } = options;
  let headers = {};
  if (!image) {
    headers = { "Content-Type": "application/json" };
  }

  if (token) {
    try {
      // Safety check - ensure token is a string
      const tokenStr = String(token);
      
      // IMPORTANT: Our testing showed the backend expects raw tokens WITHOUT 'Bearer ' prefix
      // Remove Bearer prefix if it exists
      const authToken = tokenStr.startsWith('Bearer ') ? tokenStr.substring(7) : tokenStr;
      headers = { ...headers, Authorization: authToken };
      
      // Log the complete headers for debugging
      console.log('fetchUtil: Authorization header set:', authToken.substring(0, 10) + '...');
    } catch (err) {
      console.error('fetchUtil: Error processing token:', err.message, 'Token type:', typeof token);
    }
  } else {
    console.warn('fetchUtil: No token provided for authenticated request');
  }

  if (!externalApi) {
    return fetch(`${Config.env().API_URL}${url}`, {
      method,
      headers,
      body,
      credentials,
      ...(abortSignal && { signal: abortSignal }),
    }).then(handleFetchError);
  } else {
    return fetch(`${Config.env().API}${url}`, {
      method,
      headers,
      body,
      credentials,
      ...(abortSignal && { signal: abortSignal }),
    }).then(handleFetchError);
  }
}

export { fetchUtil };
